/**
 * OpsKnowledgeEditorPage — create or edit a Knowledge Center article.
 * Route: /ops/knowledge/new  |  /ops/knowledge/:id
 * Executive Director only.
 *
 * Article body accepts rich HTML (h2/h3, tables, images, blockquotes,
 * info boxes via <div class="kc-info">…</div>). A live preview is
 * available in a side pane. Optional fields cover FAQ, references,
 * related-article slugs, hero image URL, and per-article CTA override.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import OpsLayout from './OpsLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/knowledge`;
const kcApi = axios.create({ baseURL: API, withCredentials: true });

const emptyForm = () => ({
  title: '',
  slug: '',
  category_id: '',
  author_name: 'فريق آفاق الطفولة',
  excerpt: '',
  body_html: '',
  hero_image_url: '',
  tags: [],
  references: [],
  faqs: [],
  related_slugs: [],
  status: 'draft',
  featured: false,
  order: 0,
  cta_title: '',
  cta_body: '',
});

const OpsKnowledgeEditorPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [form, setForm] = useState(emptyForm());
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState('editor');

  useEffect(() => {
    (async () => {
      const { data } = await kcApi.get('/admin/categories');
      setCats(data.categories || []);
      if (isEdit) {
        try {
          const res = await kcApi.get(`/admin/articles/${id}`);
          setForm({ ...emptyForm(), ...res.data });
        } catch {
          setErr('تعذّر تحميل المقال');
        }
      } else if (data.categories?.[0]) {
        setForm((f) => ({ ...f, category_id: data.categories[0].id }));
      }
      setLoading(false);
    })();
  }, [id, isEdit]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async (statusOverride) => {
    setErr('');
    setBusy(true);
    try {
      const payload = { ...form };
      if (statusOverride) payload.status = statusOverride;
      // Sanitize
      payload.tags = (payload.tags || []).filter(Boolean);
      payload.references = (payload.references || []).filter(Boolean);
      payload.related_slugs = (payload.related_slugs || []).filter(Boolean);
      payload.faqs = (payload.faqs || []).filter((f) => f.question && f.answer);

      const req = isEdit
        ? kcApi.put(`/admin/articles/${id}`, payload)
        : kcApi.post('/admin/articles', payload);
      const { data } = await req;

      if (!isEdit) {
        navigate(`/ops/knowledge/${data.id}`, { replace: true });
      } else {
        setForm({ ...emptyForm(), ...data });
      }
    } catch (e) {
      setErr(e.response?.data?.detail || 'تعذّر الحفظ');
    } finally {
      setBusy(false);
    }
  };

  // FAQ / list helpers
  const addFaq = () => set({ faqs: [...(form.faqs || []), { question: '', answer: '' }] });
  const editFaq = (i, patch) => set({
    faqs: form.faqs.map((f, k) => (k === i ? { ...f, ...patch } : f)),
  });
  const removeFaq = (i) => set({ faqs: form.faqs.filter((_, k) => k !== i) });

  const preview = useMemo(() => form.body_html || '', [form.body_html]);

  if (loading) {
    return (
      <OpsLayout title={isEdit ? 'تعديل مقال' : 'مقال جديد'}>
        <p className="text-[#7A866E]">جارٍ التحميل...</p>
      </OpsLayout>
    );
  }

  return (
    <OpsLayout
      title={isEdit ? 'تعديل مقال' : 'مقال جديد'}
      subtitle="حرِّر المحتوى ثم احفظ كمسودة أو انشر"
    >
      {err && (
        <div className="mb-4 bg-[#FBEBEB] border border-[#F2C4C4] text-[#B02B2B] rounded-lg px-4 py-3 text-sm">
          {err}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex gap-2 bg-white rounded-lg border border-[#E6E0D6] p-1">
          {[
            { k: 'editor', label: 'المحرّر' },
            { k: 'preview', label: 'المعاينة' },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              data-testid={`kc-editor-tab-${t.k}`}
              className={`px-4 py-1.5 rounded-md text-[13px] font-medium transition-colors ${
                tab === t.k ? 'bg-[#1F3D2B] text-white' : 'text-[#3D512D] hover:bg-[#F3F1E9]'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to="/ops/knowledge"
            className="border border-[#1F3D2B] text-[#1F3D2B] hover:bg-[#F3F1E9] rounded-lg px-4 py-2 text-sm"
          >
            العودة
          </Link>
          <button
            onClick={() => save('draft')}
            disabled={busy}
            data-testid="kc-save-draft-btn"
            className="border border-[#1F3D2B] text-[#1F3D2B] hover:bg-[#F3F1E9] rounded-lg px-4 py-2 text-sm font-bold disabled:opacity-50"
          >
            حفظ كمسودة
          </button>
          <button
            onClick={() => save('published')}
            disabled={busy}
            data-testid="kc-publish-btn"
            className="bg-[#1F3D2B] hover:bg-[#163020] text-white rounded-lg px-4 py-2 text-sm font-bold disabled:opacity-50"
          >
            {form.status === 'published' ? 'تحديث ونشر' : 'نشر'}
          </button>
        </div>
      </div>

      {tab === 'editor' ? (
        <div className="grid lg:grid-cols-[1fr_320px] gap-6">
          {/* Main editor */}
          <div className="bg-white rounded-2xl border border-[#E6E0D6] p-6 space-y-5">
            <Field label="عنوان المقال" required>
              <input
                type="text"
                value={form.title}
                onChange={(e) => set({ title: e.target.value })}
                data-testid="kc-input-title"
                className="w-full border border-[#E6E0D6] rounded-lg px-3 py-2 text-[15px] focus:outline-none focus:border-[#4A641F]"
              />
            </Field>

            <Field label="الرابط (Slug)" hint="اتركه فارغاً ليُولَّد تلقائياً من العنوان">
              <input
                type="text"
                value={form.slug}
                onChange={(e) => set({ slug: e.target.value })}
                data-testid="kc-input-slug"
                className="w-full border border-[#E6E0D6] rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#4A641F]"
              />
            </Field>

            <Field label="مقتطف قصير (Excerpt)">
              <textarea
                rows={3}
                value={form.excerpt}
                onChange={(e) => set({ excerpt: e.target.value })}
                data-testid="kc-input-excerpt"
                className="w-full border border-[#E6E0D6] rounded-lg px-3 py-2 text-sm leading-7 focus:outline-none focus:border-[#4A641F]"
              />
            </Field>

            <Field label="نص المقال (HTML)" hint="راجع دليل الأنماط أسفل — كل الأنماط جاهزة للنسخ واللصق">
              <textarea
                rows={22}
                value={form.body_html}
                onChange={(e) => set({ body_html: e.target.value })}
                data-testid="kc-input-body"
                className="w-full border border-[#E6E0D6] rounded-lg px-3 py-3 text-sm leading-7 font-mono focus:outline-none focus:border-[#4A641F]"
                dir="ltr"
                style={{ minHeight: '380px' }}
              />
            </Field>

            {/* Snippet reference — permanent template patterns */}
            <details className="border-t border-[#F0EBE2] pt-4">
              <summary className="cursor-pointer text-[13px] font-bold text-[#1F3D2B] mb-2">
                📎 دليل أنماط القالب (اضغطي للتوسيع)
              </summary>
              <div className="mt-3 space-y-2 text-[12px] leading-6 text-[#3D512D]">
                <p>انسخي أياً من هذه القوالب والصقيها داخل حقل النص أعلاه:</p>
                <pre dir="ltr" className="bg-[#FAFAF7] border border-[#E6E0D6] rounded p-3 text-[11px] leading-6 overflow-x-auto whitespace-pre-wrap">
{`<!-- ملخّص تنفيذي -->
<div class="kc-summary">
  <div class="kc-summary-label">الملخص التنفيذي</div>
  <p>...</p>
</div>

<!-- صناديق معلومات ملوّنة -->
<div class="kc-info">          <!-- بيج (عام) -->
<div class="kc-did-you-know">  <!-- ذهبي: هل تعلم؟ -->
<div class="kc-tip">           <!-- أخضر: نصيحة تطبيقية -->
<div class="kc-warning">       <!-- برتقالي: خطأ شائع -->
<div class="kc-note">          <!-- ملاحظة -->
  <span class="kc-callout-label">هل تعلم؟</span>
  المحتوى هنا...
</div>

<!-- اقتباس بارز -->
<p class="kc-pullquote">اقتباس ذهبي بحجم كبير...</p>

<!-- شبكة معلومات (Hex Grid) -->
<div class="kc-hex-grid">
  <div class="kc-hex-tile"><span class="kc-hex-tile-num">01</span>
    <h5>العنوان</h5><p>الوصف القصير</p>
  </div>
  <!-- كرّري لكل بلاطة... -->
</div>

<!-- خطوات مرقّمة (Journey) -->
<div class="kc-flow">
  <div class="kc-flow-step"><h4>الخطوة</h4><p>الشرح</p></div>
</div>

<!-- بطاقتا مقارنة -->
<div class="kc-compare">
  <div class="kc-compare-card kc-compare-green"><h4>يمكن الآن</h4><ul><li>...</li></ul></div>
  <div class="kc-compare-card kc-compare-orange"><h4>يحتاج تكييفاً</h4><ul><li>...</li></ul></div>
</div>

<!-- الرسائل الرئيسية -->
<div class="kc-takeaways">
  <h3>خمس رسائل جوهرية</h3>
  <ol><li><strong>الرسالة الأولى</strong> شرح...</li></ol>
</div>

<!-- عناوين ومحتوى قياسي -->
<h2>عنوان رئيسي</h2>  <h3>عنوان فرعي</h3>  <h4>عنوان أدنى</h4>
<blockquote>اقتباس</blockquote>
<table><thead>...</thead><tbody>...</tbody></table>
<img src="/knowledge/اسم-الصورة.svg" alt="وصف" />
`}
                </pre>
              </div>
            </details>

            {/* FAQs */}
            <div className="border-t border-[#F0EBE2] pt-5">
              <div className="flex items-center justify-between mb-3">
                <label className="font-bold text-[#1F3D2B] text-sm">الأسئلة الشائعة</label>
                <button
                  type="button"
                  onClick={addFaq}
                  className="text-[12px] text-[#1F3D2B] font-bold border-b border-[#B08A50]"
                >
                  + إضافة سؤال
                </button>
              </div>
              <div className="space-y-3">
                {(form.faqs || []).map((f, i) => (
                  <div key={i} className="border border-[#E6E0D6] rounded-lg p-3 space-y-2">
                    <input
                      value={f.question}
                      onChange={(e) => editFaq(i, { question: e.target.value })}
                      placeholder="السؤال"
                      className="w-full border-0 border-b border-[#F0EBE2] px-0 py-1 text-sm focus:outline-none focus:border-[#4A641F] bg-transparent"
                    />
                    <textarea
                      rows={2}
                      value={f.answer}
                      onChange={(e) => editFaq(i, { answer: e.target.value })}
                      placeholder="الإجابة"
                      className="w-full border-0 px-0 py-1 text-sm focus:outline-none bg-transparent resize-none"
                    />
                    <button
                      type="button"
                      onClick={() => removeFaq(i)}
                      className="text-[11px] text-[#B02B2B]"
                    >
                      حذف
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* References + related */}
            <div className="grid sm:grid-cols-2 gap-4 border-t border-[#F0EBE2] pt-5">
              <Field label="المراجع (سطر لكل مرجع)">
                <textarea
                  rows={4}
                  value={(form.references || []).join('\n')}
                  onChange={(e) =>
                    set({ references: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean) })
                  }
                  className="w-full border border-[#E6E0D6] rounded-lg px-3 py-2 text-sm leading-7 focus:outline-none focus:border-[#4A641F]"
                />
              </Field>
              <Field label="مقالات ذات صلة (Slugs — سطر لكل مقال)">
                <textarea
                  rows={4}
                  value={(form.related_slugs || []).join('\n')}
                  onChange={(e) =>
                    set({ related_slugs: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean) })
                  }
                  className="w-full border border-[#E6E0D6] rounded-lg px-3 py-2 text-sm leading-7 font-mono focus:outline-none focus:border-[#4A641F]"
                />
              </Field>
            </div>

            <Field label="الوسوم (مفصولة بفواصل)">
              <input
                type="text"
                value={(form.tags || []).join(', ')}
                onChange={(e) =>
                  set({ tags: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })
                }
                className="w-full border border-[#E6E0D6] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#4A641F]"
              />
            </Field>

            {/* CTA override */}
            <div className="border-t border-[#F0EBE2] pt-5">
              <p className="text-xs text-[#7A866E] mb-3">
                صندوق التواصل في نهاية المقال — اتركهما فارغَين لاستخدام النص الافتراضي.
              </p>
              <Field label="عنوان صندوق التواصل (اختياري)">
                <input
                  type="text"
                  value={form.cta_title || ''}
                  onChange={(e) => set({ cta_title: e.target.value })}
                  className="w-full border border-[#E6E0D6] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#4A641F]"
                />
              </Field>
              <Field label="نص صندوق التواصل (اختياري)">
                <textarea
                  rows={3}
                  value={form.cta_body || ''}
                  onChange={(e) => set({ cta_body: e.target.value })}
                  className="w-full border border-[#E6E0D6] rounded-lg px-3 py-2 text-sm leading-7 focus:outline-none focus:border-[#4A641F]"
                />
              </Field>
            </div>
          </div>

          {/* Sidebar meta */}
          <aside className="space-y-4">
            <MetaCard title="التصنيف والحالة">
              <Field label="التصنيف" required>
                <select
                  value={form.category_id}
                  onChange={(e) => set({ category_id: e.target.value })}
                  data-testid="kc-input-category"
                  className="w-full border border-[#E6E0D6] rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#4A641F]"
                >
                  <option value="">— اختر —</option>
                  {cats.map((c) => (
                    <option key={c.id} value={c.id}>{c.name_ar}</option>
                  ))}
                </select>
              </Field>
              <Field label="الحالة">
                <select
                  value={form.status}
                  onChange={(e) => set({ status: e.target.value })}
                  className="w-full border border-[#E6E0D6] rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#4A641F]"
                >
                  <option value="draft">مسودة</option>
                  <option value="published">منشور</option>
                </select>
              </Field>
              <label className="flex items-center gap-2 text-sm text-[#3D512D] cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.featured}
                  onChange={(e) => set({ featured: e.target.checked })}
                  data-testid="kc-input-featured"
                  className="w-4 h-4 accent-[#1F3D2B]"
                />
                تحديد كمقال مميّز
              </label>
              <Field label="الترتيب (0 = افتراضي)">
                <input
                  type="number"
                  value={form.order}
                  onChange={(e) => set({ order: parseInt(e.target.value, 10) || 0 })}
                  className="w-full border border-[#E6E0D6] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#4A641F]"
                />
              </Field>
            </MetaCard>

            <MetaCard title="الصورة الرئيسية">
              <Field label="رابط الصورة (URL)">
                <input
                  type="url"
                  value={form.hero_image_url}
                  onChange={(e) => set({ hero_image_url: e.target.value })}
                  data-testid="kc-input-hero"
                  placeholder="https://..."
                  dir="ltr"
                  className="w-full border border-[#E6E0D6] rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#4A641F]"
                />
              </Field>
              {form.hero_image_url && (
                <img
                  src={form.hero_image_url}
                  alt="hero"
                  className="w-full rounded-lg mt-2 border border-[#E6E0D6]"
                />
              )}
            </MetaCard>

            <MetaCard title="الكاتب">
              <input
                type="text"
                value={form.author_name}
                onChange={(e) => set({ author_name: e.target.value })}
                className="w-full border border-[#E6E0D6] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#4A641F]"
              />
            </MetaCard>
          </aside>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-[#E6E0D6] p-8">
          <p className="text-[11px] tracking-[0.2em] uppercase text-[#8B6F3E] mb-3">
            {cats.find((c) => c.id === form.category_id)?.name_ar || 'بدون تصنيف'}
          </p>
          <h1 className="font-['Noto_Kufi_Arabic'] font-extrabold text-[#1F3D2B] text-3xl leading-tight mb-4">
            {form.title || 'عنوان المقال'}
          </h1>
          {form.excerpt && <p className="text-[#3D512D] leading-8 mb-6">{form.excerpt}</p>}
          {form.hero_image_url && (
            <img src={form.hero_image_url} alt="" className="w-full rounded-xl mb-6" />
          )}
          <div
            className="kc-prose"
            dir="rtl"
            dangerouslySetInnerHTML={{ __html: preview }}
          />
        </div>
      )}
    </OpsLayout>
  );
};

const Field = ({ label, hint, required, children }) => (
  <label className="block">
    <div className="flex items-baseline justify-between mb-1.5">
      <span className="text-[13px] font-bold text-[#1F3D2B]">
        {label} {required && <span className="text-[#B02B2B]">*</span>}
      </span>
      {hint && <span className="text-[10px] text-[#7A866E]">{hint}</span>}
    </div>
    {children}
  </label>
);

const MetaCard = ({ title, children }) => (
  <div className="bg-white rounded-2xl border border-[#E6E0D6] p-4 space-y-3">
    <h4 className="text-[11px] tracking-[0.2em] uppercase text-[#8B6F3E] font-bold">{title}</h4>
    {children}
  </div>
);

export default OpsKnowledgeEditorPage;
